import { useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import Masthead from "./components/Masthead";
import Footer from "./components/Footer";
import FloatingActions from "./components/FloatingActions";
import Seo from "./seo/Seo";

import HomePage from "./pages/HomePage";
import PhysiotherapyPage from "./pages/PhysiotherapyPage";
import ServicePage from "./pages/ServicePage";
import CategoryPage from "./pages/CategoryPage";
import FitnessPage from "./pages/FitnessPage";
import ConditionsPage from "./pages/ConditionsPage";
import ContactPage from "./pages/ContactPage";
import FaqPage from "./pages/FaqPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import NotFoundPage from "./pages/NotFoundPage";
import AdminPage from "./pages/AdminPage";

import { services, categories } from "./data/services";

const BRAND = "Dr. Aakash Sharma";

/* Static page metadata. Service and blog pages are resolved
   dynamically below, so adding one needs no change here. */
export const meta = {
  "/": {
    label: "Home",
    title: `Physiotherapist in Gurgaon | ${BRAND}`,
    description:
      "Physiotherapist in Gurugram for back pain, neck pain, sports injuries and post-surgical rehab. Assessment-led treatment, clinic and home visits. Book on WhatsApp.",
  },
  "/physiotherapy": {
    label: "Physiotherapy",
    title: `Physiotherapy in Gurgaon | ${BRAND}`,
    description:
      "Orthopedic, sports, neurological, geriatric, postural and vestibular physiotherapy in Gurugram. Assessment-led rehabilitation, clinic and home visits.",
  },
  "/osteopathy-chiropractic": {
    label: "Osteopathy & Chiropractic",
    title: `Osteopathy & Chiropractic in Gurgaon | ${BRAND}`,
    description:
      "Spinal alignment, joint restoration, chronic back, neck and sciatica relief, craniosacral therapy and manual mobility work in Gurugram.",
  },
  "/oriental-medicine": {
    label: "Oriental Medicine",
    title: `Acupuncture & Cupping in Gurgaon | ${BRAND}`,
    description:
      "Acupuncture, electro-acupuncture, microcurrent, moxibustion, cupping, Gua Sha, IASTM and reflexology in Gurugram — alongside clinical physiotherapy.",
  },
  "/fitness-programs": {
    label: "Fitness Programs",
    title: `Clinical Fitness & Conditioning, Gurgaon | ${BRAND}`,
    description:
      "Clinically programmed strength, functional training, therapeutic yoga, Tai Chi and return-to-sport conditioning in Gurugram.",
  },
  "/conditions": {
    label: "Conditions Treated",
    title: `Back & Neck Pain Treatment in Gurgaon | ${BRAND}`,
    description:
      "Treatment for cervical pain, low back pain and sciatica, slipped disc, frozen shoulder, sports injuries and post-stroke rehabilitation in Gurugram.",
  },
  "/blog": {
    label: "Health Insights",
    title: `Health Insights & Articles | ${BRAND}`,
    description:
      "Plain explanations of pain, movement and recovery from a practising physiotherapist in Gurugram.",
  },
  "/faq": {
    label: "FAQ",
    title: `Physiotherapy FAQs, Gurgaon | ${BRAND}`,
    description:
      "Do you need a referral? How many sessions? Home visits in Gurugram? Fees? Common questions about physiotherapy answered.",
  },
  "/contact": {
    label: "Book an Appointment",
    title: `Book a Physiotherapist in Gurgaon | ${BRAND}`,
    description:
      "Request a physiotherapy appointment in Gurugram. Clinic and home visits across DLF, Golf Course Road, Sohna Road and Sushant Lok. Confirmed personally on WhatsApp.",
  },
};

const FALLBACK = {
  label: "Not found",
  title: `Page not found | ${BRAND}`,
  description: "That page is not here.",
};

/** Resolves metadata for static, service and blog routes alike. */
function resolveMeta(pathname) {
  if (meta[pathname]) return { ...meta[pathname], known: true };

  if (pathname.startsWith("/admin-aakash")) {
    // known: false keeps it noindex (also blocked in robots.txt), but
    // with a real title rather than the generic "Page not found"
    return {
      known: false,
      label: "Admin",
      title: `Admin | ${BRAND}`,
      description: "Private admin area.",
    };
  }

  const service = pathname.startsWith("/physiotherapy/")
    ? services.find((s) => `/physiotherapy/${s.slug}` === pathname)
    : null;

  if (service) {
    return {
      known: true,
      label: service.title,
      title: `${service.title} in Gurgaon | ${BRAND}`,
      description: `${service.short} Assessment-led treatment in Gurugram by ${BRAND}.`.slice(
        0,
        300
      ),
    };
  }

  if (pathname.startsWith("/blog/")) {
    /* Post data lives in Firestore and loads asynchronously, so this
       can't resolve the real title synchronously here. BlogPostPage
       renders its own <Seo> once the post has loaded, which overwrites
       this placeholder — see src/pages/BlogPostPage.jsx. */
    return {
      known: true,
      label: "Article",
      title: `Article | ${BRAND}`,
      description: "Health insights from Dr. Aakash Sharma.",
    };
  }

  return { ...FALLBACK, known: false };
}

function RouteChange({ mainRef }) {
  const { pathname } = useLocation();
  const first = useRef(true);
  const m = resolveMeta(pathname);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    /* preventScroll: focusing #main otherwise scrolls it to the very
       top of the viewport, tucking its heading under the sticky bar. */
    mainRef.current?.focus({ preventScroll: true });
  }, [pathname, mainRef]);

  return (
    <Seo
      title={m.title}
      description={m.description}
      pathname={pathname}
      label={m.label}
      includeFaq={pathname === "/faq"}
      noindex={!m.known}
    />
  );
}

function Shell() {
  const mainRef = useRef(null);
  const { pathname } = useLocation();
  // the admin console is a deliberately separate surface — no public
  // nav, footer or WhatsApp/call rail around it
  const isAdmin = pathname.startsWith("/admin-aakash");

  if (isAdmin) {
    return (
      <>
        <RouteChange mainRef={mainRef} />
        <AdminPage />
      </>
    );
  }

  return (
    <>
      <a href="#main" className="u-skip">
        Skip to content
      </a>

      <RouteChange mainRef={mainRef} />
      <Masthead />

      <main id="main" ref={mainRef} tabIndex={-1}>
        <Routes>
          <Route path="/" element={<HomePage />} />

          {/* Physiotherapy: landing page plus a page per service */}
          <Route path="/physiotherapy" element={<PhysiotherapyPage />} />
          <Route path="/physiotherapy/:slug" element={<ServicePage />} />

          {/* Single-page categories */}
          <Route
            path={`/${categories.osteopathy.slug}`}
            element={<CategoryPage categoryKey="osteopathy" />}
          />
          <Route
            path={`/${categories.oriental.slug}`}
            element={<CategoryPage categoryKey="oriental" tone="dark" />}
          />
          {/* Fitness has its own phase-based layout, not the shared grid */}
          <Route
            path={`/${categories.fitness.slug}`}
            element={<FitnessPage />}
          />

          <Route path="/conditions" element={<ConditionsPage />} />
          {/* About is now a section on the homepage, not a route */}
          <Route path="/about" element={<Navigate to="/" replace />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* Superseded URLs — keep old links and shares alive */}
          <Route path="/treatments" element={<Navigate to="/physiotherapy" replace />} />
          <Route path="/treatments-and-therapies" element={<Navigate to="/physiotherapy" replace />} />
          <Route path="/training" element={<Navigate to="/fitness-programs" replace />} />
          <Route path="/oriental" element={<Navigate to="/oriental-medicine" replace />} />
          <Route path="/book" element={<Navigate to="/contact" replace />} />
          <Route path="/appointment" element={<Navigate to="/contact" replace />} />
          <Route path="/faqs" element={<Navigate to="/faq" replace />} />
          <Route path="/blogs" element={<Navigate to="/blog" replace />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      <Footer />
      <FloatingActions />
    </>
  );
}

export default function App() {
  /* BrowserRouter, not HashRouter: crawlers discard everything after
     "#", so hash routes collapse the whole site into one indexable
     URL. basename comes from PUBLIC_URL, derived from package.json's
     homepage — empty on a custom domain (root), a /repo prefix on a
     GitHub Pages project URL. public/404.html restores deep links
     on refresh either way. */
  return (
    <BrowserRouter basename={process.env.PUBLIC_URL || "/"}>
      <Shell />
    </BrowserRouter>
  );
}
